import { existingResourceNameSelector } from "containers/Resources/Resources.selectors";
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { actions as dataStoreAction } from "utils/DataStore/DataStore.reducer";

interface UseCheckExistingResourceNameParams {
    /** Reference to the form instance for configuration management */
    formRef: React.MutableRefObject<any>;
}

/**
 * Interface for the hook return values
 */
interface UseCheckExistingResourceNameReturn {
    /** Function to check if a resource name already exists */
    findExistingResourceName: (name: string, event?: any) => Promise<any> | undefined;
    /** Name of the existing resource if found */
    existingResourceName: string | undefined;
    /** Type of the existing resource if found */
    existingResourceType: string | undefined;
    /** Whether the name check is currently running */
    isCheckRunning: boolean;
}

/**
 * Custom hook for checking if a resource name already exists in the system.
 *
 * This hook provides functionality to:
 * - Check resource name uniqueness before saving
 * - Manage form configuration during the check process
 * - Handle save button state during validation
 * - Provide feedback about existing resources
 *
 * The hook prevents form destruction during the check to maintain form state
 * and automatically handles save button re-enabling if the check fails.
 *
 * @param formRef - Reference to the form instance for configuration management
 * @returns Object containing the check function and related state
 */
export const useCheckExistingResourceName = ({
    formRef,
}: UseCheckExistingResourceNameParams): UseCheckExistingResourceNameReturn => {
    const dispatch = useDispatch();
    const [isCheckRunning, setCheckRunning] = useState(false);

    /**
     * Check if a resource name already exists in the system
     *
     * This function:
     * - Prevents form destruction during the check
     * - Dispatches a GraphQL query to check for existing resources
     * - Handles save button state if the check fails
     * - Restores form configuration after the check
     *
     * @param name - The resource name to check for uniqueness
     * @param event - The blur event that triggered the check (optional)
     * @returns Promise that resolves with existing resource data or undefined if no check performed
     */
    const findExistingResourceName = useCallback(
        (name, event) => {
            // Prevent form destruction during the check to maintain form state
            if (formRef) {
                formRef.current.setConfig("destroyOnUnregister", false);
            }

            // Only perform the check if a name is provided
            if (name?.length) {
                return new Promise((resolve, reject) => {
                    setCheckRunning(true);

                    // Dispatch GraphQL query to check for existing resource with the same name
                    return dispatch({
                        promise: { resolve, reject },
                        type: dataStoreAction.START_FETCH_DATA,
                        key: "isNameExist",
                        params: {
                            query: `query($name: String!) {
                            resource(by: "name", id: $name){
                                name
                                resource_type
                            }
                        }`,
                            variables: {
                                name,
                            },
                            allow_normal: true,
                            allow_deleted: true,
                        },
                    });
                })
                    .catch(() => {
                        setCheckRunning(false);

                        // Handle save button re-enabling if the check fails
                        if (event?.relatedTarget) {
                            const isSaveButton =
                                event.relatedTarget.ariaLabel?.toLowerCase()?.includes("save") ||
                                event.relatedTarget.innerText?.toLowerCase()?.includes("save") ||
                                event.relatedTarget.type === "submit";

                            // Re-enable and trigger save button if it was disabled during the check
                            if (isSaveButton) {
                                event.relatedTarget.removeAttribute("disabled");
                                event.relatedTarget.click();
                            }
                        }
                    })
                    .finally(() => setCheckRunning(false));
            }

            // Restore form configuration after the check is complete
            if (formRef) {
                formRef.current.setConfig("destroyOnUnregister", true);
            }
        },
        [dispatch, formRef]
    );

    // Get existing resource data from Redux store
    const existingResource = useSelector(existingResourceNameSelector);
    const existingResourceName = existingResource?.name;
    const existingResourceType = existingResource?.resource_type;

    return {
        findExistingResourceName,
        existingResourceName,
        existingResourceType,
        isCheckRunning,
    };
};
